import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Card,
  CardContent,
  Alert,
  Fade,
  alpha,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Warning,
  Business,
  AccountBalance,
  Gavel,
  Block,
  Security,
  CheckCircle,
  Edit,
  Search,
  Clear,
} from '@mui/icons-material';
import { BankRow, BlacklistEntry } from '../types/bankdetails';

interface BankBlacklistDialogProps {
  open: boolean;
  onClose: () => void;
  bank: BankRow | null;
  onEdit?: (entry: BlacklistEntry) => void;
}

export const BankBlacklistDialog: React.FC<BankBlacklistDialogProps> = ({
  open,
  onClose,
  bank,
  onEdit,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    if (!open) return;
    setSearchTerm('');
  }, [open, bank?.ifscCode]);

  const merchants = useMemo(() => bank?.blacklistedMerchants || [], [bank]);
  const filteredMerchants = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return merchants;
    return merchants.filter((entry) => {
      const id = typeof entry === 'string' ? entry : entry.merchantId;
      return (id || '').toLowerCase().includes(q);
    });
  }, [merchants, searchTerm]);

  if (!bank) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <Fade in timeout={300}>
        <Box>
          <DialogTitle sx={{ 
            m: 0, 
            p: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: alpha(theme.palette.error.main, 0.04),
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main'
              }}>
                <Gavel sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: 'grey.900' }}>
                  Blacklisted Merchants
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Restricted merchant configurations
                </Typography>
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { 
                  bgcolor: alpha(theme.palette.action.hover, 0.08),
                  color: 'text.primary'
                }
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              {/* Bank Information Card */}
              <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 2 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AccountBalance sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900' }}>
                      Bank Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Business sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Bank Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'grey.900' }}>
                          {bank.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AccountBalance sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          IFSC Code
                        </Typography>
                        <Chip 
                          label={bank.ifscCode} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 500 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Alert Message */}
              {merchants.length > 0 ? (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3, borderRadius: 2 }}
                  icon={<Warning fontSize="inherit" />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    This bank configuration is restricted for {merchants.length} merchant{merchants.length > 1 ? 's' : ''}
                  </Typography>
                </Alert>
              ) : (
                <Alert 
                  severity="success" 
                  sx={{ mb: 3, borderRadius: 2 }}
                  icon={<CheckCircle fontSize="inherit" />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    No merchant restrictions for this bank configuration
                  </Typography>
                </Alert>
              )}

              {/* Merchants List */}
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 2 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Security sx={{ color: merchants.length > 0 ? 'error.main' : 'success.main', fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900' }}>
                        {merchants.length > 0 ? 'Restricted Merchants' : 'No Restrictions'}
                      </Typography>
                      <Chip
                        label={
                          searchTerm.trim() && merchants.length > 0
                            ? `${filteredMerchants.length} / ${merchants.length}`
                            : merchants.length
                        }
                        size="small"
                        color={merchants.length > 0 ? 'error' : 'success'}
                        variant={merchants.length > 0 ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      />
                      {merchants.length > 0 && (
                        <Box sx={{ ml: 'auto', minWidth: { xs: '100%', sm: 240 } }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Search Merchant ID"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label="clear merchant search"
                                    onClick={() => setSearchTerm('')}
                                  >
                                    <Clear fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ p: 0 }}>
                    {merchants.length > 0 ? (
                      filteredMerchants.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                              <TableCell sx={{ fontWeight: 600 }}>Merchant ID</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Paymode</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Tr Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
                              {onEdit && (
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredMerchants.map((entry, index) => {
                              const entryObj: BlacklistEntry = typeof entry === 'string' 
                                ? { 
                                    merchantId: entry, 
                                    isActive: true, 
                                    gateway: '', 
                                    bankName: bank.name, 
                                    ifscCode: bank.ifscCode 
                                  } 
                                : {
                                    ...entry,
                                    bankName: bank.name,
                                    ifscCode: bank.ifscCode
                                  };
                              
                              return (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Block sx={{ fontSize: 16, color: 'error.main' }} />
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {entryObj.merchantId}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {entryObj.paymode ? (
                                      <Chip label={entryObj.paymode} size="small" variant="outlined" />
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">All</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {entryObj.trtype ? (
                                      <Chip label={entryObj.trtype} size="small" variant="outlined" />
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">All</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={entryObj.isActive ? 'Active' : 'Inactive'} 
                                      size="small" 
                                      color={entryObj.isActive ? 'success' : 'default'}
                                      variant={entryObj.isActive ? 'filled' : 'outlined'}
                                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" sx={{ 
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}>
                                      {entryObj.reason || entryObj.remarks || '-'}
                                    </Typography>
                                  </TableCell>
                                  {onEdit && (
                                    <TableCell align="center">
                                      <Tooltip title="Edit this restriction">
                                        <IconButton
                                          size="small"
                                          onClick={() => onEdit(entryObj)}
                                          sx={{ color: 'primary.main' }}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
                          <Search sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No merchants match "{searchTerm}"
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setSearchTerm('')}
                            sx={{ mt: 1, textTransform: 'none' }}
                          >
                            Clear search
                          </Button>
                        </Box>
                      )
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" color="success.main" gutterBottom>
                          All Clear
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This bank configuration has no merchant restrictions
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Additional Information */}
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.grey[100], 0.3) }}>
            <Button 
              onClick={onClose} 
              variant="contained" 
              color="primary"
              sx={{ 
                py: 1.25,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Box>
      </Fade>
    </Dialog>
  );
};
